import { PaneBackButton } from "@/components/PaneBackButton";
import { PaneExpandButton } from "@/components/PaneExpandButton";
import { usePaneRouterContextSafe } from "@/components/PaneRouter";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ROUTES } from "@/constants/routes";
import {
  useAppNavigate,
  usePaneLocation,
  usePaneParams,
} from "@/hooks/pane-routing";
import {
  CANVAS_DETAIL_PANE_HEADER_ACTIONS_CLASS,
  CANVAS_DETAIL_PANE_HEADER_CLASS,
} from "@/panes/canvasDetailPaneHeaderClasses";
import { EntityPaneHeaderObjectActions } from "@/panes/shared/EntityPaneHeaderObjectActions";
import type { Email, EmailThread, Entity } from "@cohesive/api-entities";
import {
  getEntityData,
  IntegrationIcon,
  PaneLayout,
  PaneLayoutContent,
  PaneLayoutHeader,
  PaneLayoutHeaderActions,
  Spinner,
} from "@cohesive/ui-components";
import { ExternalLink, RefreshCw, X } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { EmailBodyViewer, EmailHeader, ThreadMessages } from "./components";
import { useEmailThreadFetch } from "./hooks/useEmailThreadFetch";
import { useGmailConnection } from "./hooks/useGmailConnection";

export function EmailDetailPane() {
  const appNavigate = useAppNavigate();
  const paneContext = usePaneRouterContextSafe();

  const { emailId } = usePaneParams(ROUTES.CANVAS_EXPERIMENTS.EMAIL_DETAIL);

  const paneLocation = usePaneLocation("rightPane");
  const entityFromState = paneLocation?.state?.entity;

  const {
    connectionId,
    isLoading: isConnectionLoading,
    hasConnection,
  } = useGmailConnection();

  // Determine if this is an email or email thread
  const isEmailThread = entityFromState?.base === "email_thread";
  const isEmail = entityFromState?.base === "email";

  // Get thread ID
  let threadId = emailId;
  if (entityFromState) {
    if (isEmailThread) {
      const threadData = entityFromState.data as EmailThread;
      threadId = threadData.id;
    } else if (isEmail) {
      const emailData = entityFromState.data as Email;
      threadId = (emailData as any).thread_id || emailData.id;
    }
  }

  // State for current message in thread
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);

  const {
    data: fullThreadMessages,
    isLoading: isPageLoading,
    error,
    refetch,
  } = useEmailThreadFetch({
    integrationConnectionId: connectionId,
    threadId: threadId || "",
    enabled: !!connectionId && !!threadId,
  });

  const displayWebLink =
    fullThreadMessages?.[currentMessageIndex]?.web_link ||
    entityFromState?.data?.web_link;

  // Open in Gmail
  const handleOpenExternal = useCallback(() => {
    if (displayWebLink) {
      window.open(displayWebLink, "_blank");
    }
  }, [displayWebLink]);

  const handleClose = useCallback(() => {
    appNavigate({ pane: "rightPane", to: null });
  }, [appNavigate]);

  const handleMessageClick = useCallback((_message: Email, index: number) => {
    setCurrentMessageIndex(index);
  }, []);

  // Resolve the email to display — prefer freshly fetched thread messages, then
  // fall back to a typed entity passed in via route state. A generic entity
  // (base "generic") yields neither, so it stays undefined until the fetch
  // lands; the loading gate below keeps a spinner up in the meantime.
  let displayEmail: Email | undefined;
  let threadMessages: Email[] | undefined;

  if (fullThreadMessages && fullThreadMessages.length > 0) {
    threadMessages = fullThreadMessages;
    displayEmail =
      fullThreadMessages[currentMessageIndex] || fullThreadMessages[0];
  } else if (entityFromState) {
    if (isEmailThread) {
      const threadData = entityFromState.data as EmailThread;
      threadMessages = threadData.messages || [];
      displayEmail = threadMessages[currentMessageIndex] || threadMessages[0];
    } else if (isEmail) {
      displayEmail = entityFromState.data as Email;
    }
  }

  // Header actions read URLs from entity data. Merge displayWebLink (which
  // prefers freshly fetched thread messages) so copy/open stay available when
  // route state is missing or stale, and synthesize an entity when opened by
  // id alone.
  const headerActionsEntity = useMemo((): Entity | undefined => {
    if (!displayEmail) return undefined;

    if (entityFromState) {
      const data = {
        ...(getEntityData(entityFromState) as Record<string, unknown>),
        ...(displayWebLink ? { web_link: displayWebLink } : {}),
      };
      return { ...entityFromState, data } as Entity;
    }

    return {
      base: "email",
      platform: "gmail",
      data: {
        ...displayEmail,
        ...(displayWebLink ? { web_link: displayWebLink } : {}),
      },
    } as Entity;
  }, [displayEmail, displayWebLink, entityFromState]);

  // Loading state — show a centered spinner while the connection resolves or
  // while we're fetching and have nothing to render yet. Keying off
  // `!displayEmail` (not `!entityFromState`) is what stops a generic entity
  // from flashing "Email not found" before its thread fetch lands.
  if (isConnectionLoading || (isPageLoading && !displayEmail)) {
    return (
      <PaneLayout>
        <PaneLayoutHeader className={CANVAS_DETAIL_PANE_HEADER_CLASS}>
          <PaneLayoutHeaderActions
            className={CANVAS_DETAIL_PANE_HEADER_ACTIONS_CLASS}
          >
            <PaneBackButton />
            <div className="flex-1" />
            <TooltipProvider>
              {paneContext && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="toolbarGhost"
                      size="icon-sm"
                      className="shrink-0"
                      onClick={handleClose}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Close</TooltipContent>
                </Tooltip>
              )}
            </TooltipProvider>
          </PaneLayoutHeaderActions>
        </PaneLayoutHeader>
        <PaneLayoutContent>
          <div className="flex h-full flex-col items-center justify-center gap-3">
            <Spinner size="lg" />
            <p className="text-sm text-muted-foreground">Loading email...</p>
          </div>
        </PaneLayoutContent>
      </PaneLayout>
    );
  }

  // No connection state
  if (!hasConnection) {
    return (
      <PaneLayout>
        <PaneLayoutHeader className={CANVAS_DETAIL_PANE_HEADER_CLASS}>
          <PaneLayoutHeaderActions
            className={CANVAS_DETAIL_PANE_HEADER_ACTIONS_CLASS}
          >
            <PaneBackButton />
            <div className="flex-1" />
            <TooltipProvider>
              {paneContext && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="toolbarGhost"
                      size="icon-sm"
                      className="shrink-0"
                      onClick={handleClose}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Close</TooltipContent>
                </Tooltip>
              )}
            </TooltipProvider>
          </PaneLayoutHeaderActions>
        </PaneLayoutHeader>
        <PaneLayoutContent>
          <div className="flex flex-col items-center justify-center h-full gap-4 p-8">
            <p className="text-muted-foreground text-center">
              Connect your Gmail account to view emails
            </p>
          </div>
        </PaneLayoutContent>
      </PaneLayout>
    );
  }

  // Error state
  if (error && !displayEmail) {
    return (
      <PaneLayout>
        <PaneLayoutHeader className={CANVAS_DETAIL_PANE_HEADER_CLASS}>
          <PaneLayoutHeaderActions
            className={CANVAS_DETAIL_PANE_HEADER_ACTIONS_CLASS}
          >
            <PaneBackButton />
            <div className="flex-1" />
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="toolbarGhost"
                    size="icon-sm"
                    className="shrink-0"
                    onClick={() => refetch()}
                  >
                    <RefreshCw className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Retry</TooltipContent>
              </Tooltip>

              {paneContext && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="toolbarGhost"
                      size="icon-sm"
                      className="shrink-0"
                      onClick={handleClose}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Close</TooltipContent>
                </Tooltip>
              )}
            </TooltipProvider>
          </PaneLayoutHeaderActions>
        </PaneLayoutHeader>
        <PaneLayoutContent>
          <div className="flex flex-col items-center justify-center h-full gap-4 p-8">
            <p className="text-muted-foreground">
              Failed to load email. Please try again.
            </p>
          </div>
        </PaneLayoutContent>
      </PaneLayout>
    );
  }

  if (!displayEmail) {
    return (
      <PaneLayout>
        <PaneLayoutHeader className={CANVAS_DETAIL_PANE_HEADER_CLASS}>
          <PaneLayoutHeaderActions
            className={CANVAS_DETAIL_PANE_HEADER_ACTIONS_CLASS}
          >
            <PaneBackButton />
            <div className="flex-1" />
            <TooltipProvider>
              {paneContext && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="toolbarGhost"
                      size="icon-sm"
                      className="shrink-0"
                      onClick={handleClose}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Close</TooltipContent>
                </Tooltip>
              )}
            </TooltipProvider>
          </PaneLayoutHeaderActions>
        </PaneLayoutHeader>
        <PaneLayoutContent>
          <div className="flex flex-col items-center justify-center h-full gap-2">
            <div className="text-muted-foreground">Email not found</div>
          </div>
        </PaneLayoutContent>
      </PaneLayout>
    );
  }

  // Check if we have full body
  const hasFullBody =
    displayEmail.body &&
    displayEmail.body !== "LOAD_THREAD_TO_VIEW_FULL_BODY_CONTENT" &&
    displayEmail.body.trim() !== "";

  return (
    <PaneLayout>
      <PaneLayoutHeader className={CANVAS_DETAIL_PANE_HEADER_CLASS}>
        <PaneLayoutHeaderActions
          className={CANVAS_DETAIL_PANE_HEADER_ACTIONS_CLASS}
        >
          <PaneBackButton />
          <div className="flex min-w-0 flex-1 items-center gap-1.5 pl-1">
            <IntegrationIcon
              platform="gmail"
              name="Gmail"
              className="h-4 w-4 shrink-0"
            />
            <span className="text-sm font-medium text-foreground truncate leading-none">
              {displayEmail.subject || "Email"}
            </span>
          </div>
          <TooltipProvider>
            {error && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="toolbarGhost"
                    size="icon-sm"
                    className="shrink-0"
                    onClick={() => refetch()}
                  >
                    <RefreshCw className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Retry loading email</TooltipContent>
              </Tooltip>
            )}

            {headerActionsEntity && (
              <EntityPaneHeaderObjectActions
                entity={headerActionsEntity}
                name={displayEmail.subject}
              />
            )}

            <PaneExpandButton />

            {paneContext && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="toolbarGhost"
                    size="icon-sm"
                    className="shrink-0"
                    onClick={handleClose}
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Close</TooltipContent>
              </Tooltip>
            )}
          </TooltipProvider>
        </PaneLayoutHeaderActions>
      </PaneLayoutHeader>

      <PaneLayoutContent noScroll={true} className="!p-0">
        <div className="flex flex-col h-full">
          {/* Email header (sender, recipients, etc.) */}
          <div className="border-b bg-background px-6 py-4">
            <EmailHeader
              senderName={displayEmail.sender_name}
              senderAddress={displayEmail.sender_address}
              recipients={displayEmail.recipients}
              subject={displayEmail.subject}
              timestamp={displayEmail.timestamp}
              hasAttachments={displayEmail.has_attachments}
            />
          </div>

          {/* Email body viewer */}
          <div className="flex-1 min-h-0 overflow-hidden">
            {isPageLoading && !hasFullBody ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center text-muted-foreground">
                  <div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin mx-auto mb-3" />
                  <p>Loading email content...</p>
                </div>
              </div>
            ) : !hasFullBody ? (
              <div className="flex items-center justify-center h-full p-6">
                <div className="text-center text-muted-foreground max-w-md">
                  <p className="mb-4">Email preview only available</p>
                  <p className="text-sm mb-6 whitespace-pre-wrap">
                    {displayEmail.body_preview}
                  </p>
                  <Button
                    variant="outline"
                    onClick={handleOpenExternal}
                    className="gap-2"
                  >
                    <ExternalLink className="h-4 w-4" />
                    Open in Gmail
                  </Button>
                </div>
              </div>
            ) : (
              <EmailBodyViewer body={displayEmail.body} />
            )}
          </div>

          {/* Thread messages (if email thread) */}
          {threadMessages && threadMessages.length > 1 && (
            <ThreadMessages
              messages={threadMessages}
              onMessageClick={handleMessageClick}
              currentMessageIndex={currentMessageIndex}
            />
          )}
        </div>
      </PaneLayoutContent>
    </PaneLayout>
  );
}
